import { COLLECTIONS, MUTATE_OPERATIONS_SENT } from "constants/action-types";
import { formatMessage } from "lib/globalize-util";
import { snackbarAdd } from "actions/snackbars";
import { Observable } from "rxjs";
import { getAndIncrementRequestId, mapToOperation } from "./helpers";
import { ajax } from "lib/observable";
import siteConfig from "site-config";
import { List, Map, fromJS } from "immutable";
import { listInsertOperation, listRemoveOperation, listSetOperation } from "actions/mutate";
import { DEFAULT_MASTER_VIEW_NAME, DEFAULT_VIEW_NAME, initCurrentView } from "actions/collections";
import { replace } from "react-router-redux";
import { mergeWithDefaultSchema, parseAssortmentId } from "helpers/product-collection";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentEvents, AssortmentTransactions } from "constants/sentry-metrics";
import { CollectionKeys } from "constants/product-collection";
import { APIError } from "lib/errors";

const mapOperationToThisEndpoint = (operation) => {
    let operationName: string;
    if (operation.insert) {
        operationName = "insert";
    } else if (operation.remove) {
        operationName = "remove";
    } else if (operation.move) {
        operationName = "move";
    } else if (operation.set) {
        operationName = "set";
    }
    return {
        [`${operationName}_item_display_preference`]: operation[operationName],
    };
};

const changeViewToDefault = (store, id: string) => {
    const state = store.getState();
    const { isRollupMode } = parseAssortmentId(id);
    const collection = state.mutate.snapshots && state.mutate.snapshots.get(id);
    const itemDisplayProperty = collection ? collection.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS) : undefined;
    const displayPreferencesId: string = collection
        ? collection.getIn(["item_display_preference_object_id", "ref", "object_id"])
        : undefined;
    const displayPreferences = state.mutate.snapshots && state.mutate.snapshots.get(displayPreferencesId);
    const defaultView =
        displayPreferences && displayPreferences.find((preference) => preference.get("name") === DEFAULT_VIEW_NAME);
    const defaultViewArray = !isRollupMode && defaultView ? defaultView.get("display_preferences")?.toJS() : [];
    const { schema } = mergeWithDefaultSchema(defaultViewArray, itemDisplayProperty, false, !!defaultView);
    return [initCurrentView(DEFAULT_MASTER_VIEW_NAME, schema)];
};

// listen for the collection to be fetched, then set the current view to the default from the collection
export const initDefaultViewEpic = (action$, store) =>
    action$.ofType(COLLECTIONS.COLLECTION.FETCH.COMPLETED).concatMap(({ payload, error }) => {
        if (error) {
            if (payload.code === 403 || payload.code === 404) {
                return Observable.of(replace(`/merchants?redirect=${location.pathname}${location.search}`));
            }

            if (payload.code === 400 && payload.message.includes("the requester merchant is deactivated")) {
                return Observable.of(replace("/merchant-deactivated"));
            }

            throw new Error(payload);
        }

        const {
            collection: { id },
            merchantId,
        } = payload;

        return ajax(`${siteConfig.goAPI}/v1/merchants/${merchantId}`)
            .flatMap(({ response }) => {
                if (response.deactivated) {
                    return Observable.of(replace("/merchant-deactivated"));
                }

                return Observable.from(changeViewToDefault(store, id));
            })
            .catch((error) => {
                throw new Error(
                    error.xhr ? new APIError(error.xhr.response.error, error.xhr.status, error.xhr.statusText) : error
                );
            });
    });

export const resetToDefaultViewEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.CURRENT_VIEW.RESET)
        .concatMap(({ payload: { collectionId } }) => changeViewToDefault(store, collectionId));

// add an item to display preferences (aka grid views)
// flows into syncItemDisplayPreferencesEpic after going through mutation epics
export const addItemDisplayPreferenceEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.ADD.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, displayPreference, index = 0 }) => [
            listInsertOperation(id, displayPreference, index), // always add at top of list for now
            snackbarAdd({ message: formatMessage("View saved!") }),
        ]);

// update a single display preference
// flows into syncItemDisplayPreferencesEpic after going through mutation epics
export const editItemDisplayPreferenceEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.EDIT.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, index, displayPreference }) => {
            return [
                listSetOperation(id, index, Map(displayPreference)),
                snackbarAdd({ message: formatMessage("View updated!") }),
            ];
        });

// update a merchant level saved view
export const editMerchantItemDisplayPreference = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.MERCHANT_EDIT.START)
        .map(({ payload }) => payload)
        .concatMap(({ merchantId, id, displayPreference }) => {
            const url = `/v1/collections/merchants/${merchantId}/item_display_preferences/${id}`;
            return ajax({
                body: {
                    merchant_item_display_preference: displayPreference,
                    update_mask: { paths: ["item_display_preference"] },
                },
                method: "PUT",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { merchantId, id, displayPreference },
                    type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.MERCHANT_EDIT
                        .COMPLETED,
                }))
                .concat(Observable.of(snackbarAdd({ message: "View updated successfully!" })))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error updating the view. Please try again."),
                            })
                        )
                    );
                });
        });

// update a merchant level local saved view
export const editMerchantLocalItemDisplayPreference = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.MERCHANT_LOCAL_EDIT.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, index, displayPreference }) => {
            return [
                listSetOperation(id, index, Map(displayPreference), undefined, { syncData: { isMerchantView: true } }),
                snackbarAdd({ message: formatMessage("View updated!") }),
            ];
        });

// delete a merchant level saved view
export const deleteMerchantItemDisplayPreference = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.MERCHANT_DELETE.START)
        .map(({ payload }) => payload)
        .concatMap(({ collectionId, merchantId, id, index }) => {
            const demoteUrl = `/v1/collections/${collectionId}/merchant_display_preferences/demote`;
            return ajax({
                body: { merchant_id: merchantId, index },
                method: "POST",
                url: siteConfig.goAPI + demoteUrl,
            })
                .map(() => ({
                    payload: { merchantId, id },
                    type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.MERCHANT_DELETE
                        .COMPLETED,
                }))
                .concat(Observable.of(snackbarAdd({ message: "View deleted successfully!" })))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error deleting the view. Please try again."),
                            })
                        )
                    );
                });
        })
        .catch(() => {
            return Observable.merge(
                Observable.of(
                    snackbarAdd({ message: formatMessage("There was an error deleting the view. Please try again.") })
                )
            );
        });

// remove a single display preference
// flows into syncItemDisplayPreferencesEpic after going through mutation epics
export const removeItemDisplayPreferencesEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.REMOVE.START)
        .map(({ payload }) => payload)
        .concatMap(({ id, index }) => [
            listRemoveOperation(id, index),
            snackbarAdd({ message: formatMessage("View removed.") }),
        ]);

// Retrieve the list of merchant display preferences for a merchant
export const fetchMerchantItemDisplayPreferences = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.MERCHANT_ITEM_DISPLAY_PREFERENCE.FETCH.START)
        .map(({ payload }) => payload)
        .concatMap(({ merchantId }) => {
            const url = `/v1/collections/merchants/${merchantId}/item_display_preferences`;
            return ajax({
                method: "GET",
                url: siteConfig.goAPI + url,
            })
                .map((ajaxResponse) => {
                    const {
                        response: { merchant_item_display_preferences },
                    } = ajaxResponse;
                    let merchantSavedViewsArray = List();
                    merchant_item_display_preferences.forEach((item_preference) => {
                        merchantSavedViewsArray = merchantSavedViewsArray.push(
                            fromJS(item_preference.item_display_preference)
                        );
                    });
                    return {
                        payload: { merchantId, merchantSavedViews: merchantSavedViewsArray },
                        type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.MERCHANT_ITEM_DISPLAY_PREFERENCE.FETCH
                            .COMPLETED,
                    };
                })
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage(
                                    "There was an error retrieving the merchant saved views. Please try again."
                                ),
                            })
                        )
                    );
                });
        });

// promote a single display preference
export const promoteItemDisplayPreferenceEpic = (action$, _store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.PROMOTE.START)
        .map(({ payload }) => payload)
        .concatMap(({ collectionId, merchantId, index }) => {
            const url = `/v1/collections/${collectionId}/display_preferences/promote`;
            return ajax({
                body: { merchant_id: merchantId, index },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId, merchantId, index },
                    type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.PROMOTE.COMPLETED,
                }))
                .concat(Observable.of(snackbarAdd({ message: "View promoted successfully!" })))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error promoting the view. Please try again."),
                            })
                        )
                    );
                });
        });

// sync display preference items to the server (add, update, delete, move)
// triggered after mutation epics have run
export const syncItemDisplayPreferencesEpic = (action$, store) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.SYNC.START)
        // Dont proceed if there is something sending or there is nothing to send
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId, syncData } }: { payload: { objectId: string; syncData: any } }) => {
            const { collection, mutate } = store.getState();

            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    operations.push(mapOperationToThisEndpoint(mapToOperation(operation)));
                });
            });
            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const command = {
                operations,
                revision,
                req_id: requestId,
            };

            store.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            return { command, sessionId, requestId, collectionId: committedHEAD.document_id, merchantId, syncData };
        })
        // Make it a queue, wait for response before continuing
        .concatMap(({ command, collectionId, requestId, sessionId, merchantId, syncData }) => {
            const { isMerchantView } = syncData || {};
            const url = `/v1/collections/${collectionId}/${
                isMerchantView ? "merchant_display_preferences" : "display_preferences"
            }/mutate?sid=${sessionId}&req_id=${requestId}`;
            return (
                ajax({
                    body: { ...command, merchant_id: merchantId },
                    method: "POST",
                    url: siteConfig.goAPI + url,
                })
                    .map(() => ({
                        payload: { collectionId },
                        type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.SYNC.COMPLETED,
                    }))
                    // .concat(() => (snackbarAdd({ message: "View updated successfully!" })))
                    .catch((error) => {
                        // const actions = [];

                        // if (error.status === 409) {
                        //     return Observable.of({
                        //         type: COLLECTIONS.COLLECTION.ITEMS.SYNC.CONFLICT,
                        //         meta: {
                        //             collectionId,
                        //             objectId,
                        //         },
                        //         payload: error,
                        //         error: true,
                        //     });
                        // } else if (error.status === 404) {
                        //     // attempting to modify a deleted collection
                        //     actions.push(Observable.of(push(`/assortments`)));
                        //     actions.push(
                        //         Observable.of(snackbarAdd({ message: formatMessage("This assortment is no longer available") })),
                        //     );
                        // } else {
                        //     actions.push(Observable.of({
                        //         type: COLLECTIONS.COLLECTION.ITEMS.SYNC.ERROR,
                        //         payload: error,
                        //     }));
                        // }

                        return Observable.merge(
                            // ...actions,
                            // Observable.of({
                            //     error: true,
                            //     payload: error,
                            //     type: COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.IITEMDISPLAYPREFERENCE.ADD.COMPLETED,
                            // }),
                            Observable.of(
                                snackbarAdd({
                                    message: formatMessage("There was an error saving the view. Please try again."),
                                })
                            )
                        );
                    })
            );
        });

export const stopMeasuringColumnOperationsPerformance = (action$) =>
    action$.ofType(COLLECTIONS.COLLECTION.ITEMS.ITEMDISPLAYPREFERENCES.CURRENT_VIEW.CHANGE).flatMap(() => {
        process.nextTick(() => {
            SentryUtil.finishSpan(AssortmentTransactions.AddColumn, AssortmentEvents.AddColumn);
            SentryUtil.finishTransaction(AssortmentTransactions.AddColumn);

            SentryUtil.finishSpan(AssortmentTransactions.PinColumn, AssortmentEvents.PinColumn);
            SentryUtil.finishTransaction(AssortmentTransactions.PinColumn);

            SentryUtil.finishSpan(AssortmentTransactions.MoveColumn, AssortmentEvents.MoveColumn);
            SentryUtil.finishTransaction(AssortmentTransactions.MoveColumn);

            SentryUtil.finishSpan(AssortmentTransactions.ResizeColumn, AssortmentEvents.ResizeColumn);
            SentryUtil.finishTransaction(AssortmentTransactions.ResizeColumn);
        });
        return [];
    });
